import { useCallback, useContext, useEffect, useState } from 'react';
import { Context } from '../../../../../context/AppContext';
import useLocalStorage from '../../../../../hooks/useLocalStorage';
import { MAX_SUGGESTIONS } from '../../../constants';
import { isNotEmptyArray } from '../../utils/helpers';

const createSuggestLabel = ({ keyword, selectedSearchArea }) => {
  if (!selectedSearchArea && !keyword) return undefined;
  if (!selectedSearchArea) return keyword;
  if (!keyword) return selectedSearchArea.name;
  return `${keyword} in ${selectedSearchArea.name}`;
};

const useRecentSearches = () => {
  const [searchHistory, saveSearchHistory] = useLocalStorage('searches');
  const [lastSearch, setLastSearch] = useState();
  const {
    media: { mobile },
  } = useContext(Context);
  const maxSuggestions = mobile
    ? MAX_SUGGESTIONS.MOBILE.RECENT_SEARCHES
    : MAX_SUGGESTIONS.DESKTOP.RECENT_SEARCHES;
  const [recentSearches, setRecentSearches] = useState(
    (searchHistory || []).slice(0, maxSuggestions)
  );

  /**
   * Remove legacy saved recent searches
   */
  const [storedRecentSearches] = useLocalStorage('recentSearches');
  useEffect(() => {
    if (storedRecentSearches) {
      window.localStorage.removeItem('recentSearches');
    }
  }, [storedRecentSearches]);
  const [storedSearchHistory] = useLocalStorage('searchHistory');
  useEffect(() => {
    if (storedSearchHistory) {
      window.localStorage.removeItem('searchHistory');
    }
  }, [storedSearchHistory]);

  /**
   * Save to local storage and update state. Ensure any duplicate searches are removed.
   */
  const addToRecentSearches = useCallback(
    ({ keyword, path, selectedSearchArea }) => {
      const suggestLabel = createSuggestLabel({ keyword, selectedSearchArea });
      if (suggestLabel) {
        const newEntry = {
          keyword,
          name: keyword,
          id: path,
          path,
          selectedSearchArea,
          suggestLabel,
        };
        const filteredRecentSearches = recentSearches
          .filter(existingSearch => existingSearch.id !== newEntry.id)
          .slice(0, maxSuggestions - 1);
        const updatedRecentSearches = [newEntry, ...filteredRecentSearches];
        setRecentSearches(updatedRecentSearches);
        saveSearchHistory(updatedRecentSearches);
      }
    },
    [recentSearches]
  );

  /** Update the last search term when the recent searches array is changed */
  useEffect(() => {
    if (isNotEmptyArray(recentSearches)) {
      setLastSearch(recentSearches[0]);
    } else {
      setLastSearch();
    }
  }, [recentSearches]);

  return { addToRecentSearches, lastSearch, recentSearches };
};

export default useRecentSearches;
